/**
 * One per page: the header, then the page's body in whatever state its data is in (starter-system-r1 section 8).
 * - first load: a skeleton in the page's shape   - error with no data: ErrorNote and Try again
 * - error with data: the last answer stays, with a note that says so   - data: the page draws it
 * Empty is the pattern's job (DataTable's required `empty`). The page only says what to draw once data exists.
 */
import type { ReactNode } from 'react'
import { ErrorNote } from '../patterns/ErrorNote'
import { LiveBadge } from '../patterns/LiveBadge'
import { PageHeader, type Crumb } from '../patterns/PageHeader'
import { Skeleton } from '../primitives/Skeleton'
import s from './Screen.module.css'

/** What `useLoad` returns; any data hook with this shape works. */
export interface Query<T> {
  data: T | undefined
  error: Error | undefined
  loading: boolean
  reload: () => void
}

interface Common {
  title: string
  count?: number
  description?: ReactNode
  crumb?: Crumb
  actions?: ReactNode
  /** Beside the title: a status chip. */
  after?: ReactNode
  /** How the page stays fresh: draws the LiveBadge ("every 15s"). */
  live?: { mode: 'poll' | 'stream'; label?: string }
  /** The loading shape. */
  skeleton?: 'table' | 'cards' | 'record'
}

export function Screen<T>(props: Common & ({ query: Query<T>; children: (data: T) => ReactNode } | { query?: undefined; children: ReactNode })) {
  const { title, count, description, crumb, actions, after, live, skeleton = 'table' } = props
  const head = (
    <PageHeader
      title={title}
      count={count}
      description={description}
      crumb={crumb}
      actions={actions}
      after={
        (after || live) && (
          <>
            {after}
            {live && <LiveBadge mode={live.mode} label={live.label} />}
          </>
        )
      }
    />
  )

  let body: ReactNode
  if (props.query === undefined) body = props.children
  else {
    const q = props.query
    if (q.data !== undefined) {
      body = (
        <>
          {q.error && <ErrorNote title="Couldn't refresh." reason={q.error.message} stale="Showing the last answer." onRetry={q.reload} />}
          {props.children(q.data)}
        </>
      )
    } else if (q.error) body = <ErrorNote reason={q.error.message} onRetry={q.reload} />
    else body = <Loading shape={skeleton} />
  }

  return (
    <div className={s.box}>
      <div className={s.page}>
        {head}
        {body}
      </div>
    </div>
  )
}

function Loading({ shape }: { shape: 'table' | 'cards' | 'record' }) {
  return (
    <div className={s.loading} role="status" aria-label="Loading">
      {shape === 'cards' && (
        <div className={s.cards}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} height="block" />
          ))}
        </div>
      )}
      <div className={s.sheet}>
        {(shape === 'record' ? [60, 85, 40] : [92, 78, 88, 64, 80]).map((w, i) => (
          <div key={i} className={s.skRow}>
            <Skeleton width={`${w}%`} />
            <Skeleton width={`${Math.max(30, w - 40)}%`} />
          </div>
        ))}
      </div>
    </div>
  )
}
